<template>
  <div ref="price_request_container" v-if="!$noCartMode()">
    <breadcrumbs :breadcrumbs="breadcrumbs"/>
    <div class="container">
      <h1>{{ $t('price_on_request') }}</h1>
      <div v-if="priceRequest.price_request_items?.length" class="products-list price-request">
        <div class="flex-table header-table" role="rowgroup">
          <div class="flex-row articul" role="columnheader">
            <span>{{ $t('product_code') }}</span>
          </div>
          <div class="flex-row name" role="columnheader">
            <span>{{ $t('product_name') }}</span>
          </div>
          <div class="flex-row quantity" role="columnheader">
            <span>{{ $t('quantity') }}</span>
          </div>
        </div>

        <price-request-item-component 
          v-for="item in priceRequest.price_request_items"
          :item="item"
          :key="`item-${item.id}`"
          @set-delete-item="setDeleteItem"
        />

        <div v-if="$auth.loggedIn" class="container comment">
          <div class="comment-link">
            <a href="#" @click.prevent="textareaHide = !textareaHide" :aria-label="$t('add_comment')">
              <i class="svg-icon icon-pointer" :class="{'plus': textareaHide, 'minus': !textareaHide}"></i>
              <h4>{{ $t('add_comment') }}</h4>
              <h3>{{ $t('add_comment') }}</h3>
            </a>
          </div>
          <transition name="fadeHeight" mode="out-in">
            <div class="field comment-text" v-if="!textareaHide"><textarea v-model="priceRequest.comment"/></div>
          </transition>
        </div>
        <div class="buttons">
          <div class="printer-div" @click.prevent="savePriceRequestFile">
            <i class="svg-icon save"></i><span>{{ $t('save') }}</span>
          </div>
          <button class="inverted-button" @click.prevent="showDeleteDialog = true">{{ $t('clear') }}</button>
          <button v-if="$auth.loggedIn" @click.prevent="showSubmitDialog = true">{{ $t('submit') }}</button>
          <button v-else-if="showWhereToBuy" @click.prevent="$router.push({path: localePath('/where-to-buy/')})">{{ $t('select_distributor') }}</button>
          <button v-else @click.prevent="$router.push({path: localePath('/login/')})">{{ $t('submit') }}</button>
        </div>
      </div>

      <div v-else>
        {{ $t('price_request_not_added_1') }}
        <template v-if="$auth.loggedIn">
          {{ $t('price_request_not_added_2') }}
          <nuxt-link :to="localePath('/profile/price-requests/')">{{ $t('price_requests') }}</nuxt-link>.
        </template>
      </div>

      <modal :visible="itemToDelete" @close="itemToDelete = null">
        <template v-slot:header>
          <h3>{{ $t('confirm_item_delete_1') }}<br/>{{ $t('confirm_item_delete_2') }}</h3>
        </template>
        <template v-slot:footer>
          <button class="modal-default-button" @click="deletePriceRequestItem">{{ $t('delete') }}</button>
        </template>
      </modal>

      <modal :visible="showDeleteDialog" @close="showDeleteDialog = false">
        <template v-slot:header>
          <h3>{{ $t('confirm_delete_1') }}<br/>{{ $t('confirm_delete_2') }}</h3>
        </template>
        <template v-slot:footer>
          <button class="modal-default-button" @click="deletePriceRequest">{{ $t('clear') }}</button>
        </template>
      </modal>

      <modal :visible="showSubmitDialog" @close="showSubmitDialog = false">
        <template v-slot:header>
          <h3>{{ $t('confirm_submit') }}</h3>
        </template>
        <template v-if="isRetailer" v-slot:body>
          {{ $t('checkout_distributors_list_1') }}
          {{ relatedDistributors.map(x => x.profile?.company_name).join(', ') }}.
          <br/>
          {{ $t('checkout_distributors_list_2') }}
          <nuxt-link :to="localePath('/profile/edit/')">{{ $t('checkout_distributors_list_3') }}</nuxt-link>.
        </template>
        <template v-slot:footer>
          <button class="modal-default-button" @click.prevent="sendPriceRequest">{{ $t('submit') }}</button>
        </template>
      </modal>

      <modal :visible="showSuccessModal" @close="showSuccessModal = false">
        <template v-slot:header>
          <div>{{ $t('price_request_send_header') }}</div>
        </template>
        <template v-slot:body>
          <div>{{ $t('price_request_send_text') }}</div>
        </template>
        <template v-slot:footer>
          <button class="modal-default-button"
                  @click.prevent="$router.push({path: localePath('/profile/price-requests/')})">{{
              $t('go_to_profile')
            }}
          </button>
        </template>
      </modal>
    </div>
  </div>
</template>

<script>
import {rememberExpiresMixin} from '@/mixins';
import {PriceRequest, PriceRequestItem} from '@/models/price-request';
import Breadcrumbs from '@/components/breadcrumbs';
import Modal from '@/components/modal';
import PriceRequestItemComponent from '@/components/catalog/price-request-item';
import { downloadFile } from "@/helpers";

export default {
  components: {
    Breadcrumbs,
    Modal,
    PriceRequestItemComponent,
  },
  mixins: [rememberExpiresMixin],
  data() {
    return {
      priceRequest: {},
      showWhereToBuy: false,
      itemToDelete: null,
      showDeleteDialog: false,
      showSubmitDialog: false,
      showSuccessModal: false,
      textareaHide: true,
      printWindow: false,
      distributors: [],
    }
  },
  computed: {
    breadcrumbs() {
      return [{name: this.$t('price_on_request')}];
    },
    isRetailer() {
      return this.$auth.user?.scope?.perms?.includes('can_access_retailer_account');
    },
    relatedDistributors() {
      return this.distributors.filter(x => this.$auth.user.distributors.map(y => y.distributor).includes(x.id));
    },
  },
  mounted(){
    if(this.$noCartMode()){
      this.$nuxt.context.error({statusCode: 404});
      return
    }
  },
  async fetch() {
    // [this.priceRequest, this.showWhereToBuy] = await Promise.all([
    //   this.$axios.$get('/order/price-request/latest-price-request/', {count_loading: true}),
    //   this.$axios.$get('/user/branch/show-where-to-buy/', {count_loading: true}),
    // ]);
    [this.priceRequest, this.distributors] = await Promise.all([
      this.$axios.$get('/order/price-request/latest-price-request/', {count_loading: true}),
      this.$axios.$get('/loyalpro/users/distributors/'),
    ]);
  },
  head() {
    return {
      title: this.$t('price_on_request'),
    }
  },
  methods: {
    fetchPriceRequest(scrollToTop = false) {
      this.$axios.$get('/order/price-request/latest-price-request/')
          .then((response) => {
            this.priceRequest = response;
            if (scrollToTop) {
              this.$refs.price_request_container.scrollIntoView({behavior: 'smooth'});
            }
          });
    },
    deletePriceRequest() {
      let priceRequest = new PriceRequest(this.priceRequest);
      priceRequest.delete().then(() => {
        this.$store.dispatch('setPriceRequestExists');
        this.fetchPriceRequest(true);
      }).finally(() => this.showDeleteDialog = false);
    },
    setDeleteItem(id) {
      this.itemToDelete = id;
    },
    deletePriceRequestItem() {
      let item = new PriceRequestItem({id: this.itemToDelete});
      item.delete().then(() => {
        this.$store.dispatch('setPriceRequestExists');
        this.fetchPriceRequest();
      }).finally(() => this.itemToDelete = null);
    },
    sendPriceRequest() {
      this.$axios.$post('/order/price-request/send-price-request/', {comment: this.priceRequest.comment})
        .then((response) => {
          this.$store.dispatch('setPriceRequestExists');
          this.fetchPriceRequest(true);
          this.showSuccessModal = true;
        })
        .catch((e) => {
          try {
            this.$notify({
              duration: 5000,
              text: e.response.data.detail,
              ignoreDuplicates: true,
            });
          } catch (e) {
          }
        })
        .finally(() => this.showSubmitDialog = false);
    },
    printPriceRequest() {
      window.print();
    },
    savePriceRequestFile() {
      downloadFile(this, `/order/price-request/${this.priceRequest.id}/report/`);
    },
    closePrintWindow() {
      this.printWindow = false;
    },
  },
}
</script>

<i18n lang="yaml">
ru:
  price_on_request: 'Мои запросы'
  price_request_send_header: 'Благодарим за запрос! Ваш запрос отправлен менеджеру и сохранен в Вашем личном кабинете на странице «Мои запросы»'
  price_request_send_text: 'Менеджер свяжется с Вами в ближайшее время. На адрес электронной почты, указанный при регистрации на сайте, отправлено письмо с файлом запроса. Если письмо не пришло, проверьте папку Спам.'
  go_to_profile: 'В личный кабинет'
  price_request_not_added_1: 'Новых запросов нет.'
  price_request_not_added_2: 'История ваших запросов в личном кабинете во вкладке'
  print_btn: 'Печать запроса'
  save_file_btn: 'Сохранить файл'
  add_comment: 'Добавить комментарий'
  confirm_delete_1: 'Вы уверены, что хотите очистить запрос?'
  confirm_delete_2: 'Подтвердите действие!'
  confirm_item_delete_1: 'Вы уверены, что хотите удалить этот товар?'
  confirm_item_delete_2: 'Подтвердите действие!'
  delete: 'Удалить'
  confirm_submit: 'Подтвердите, пожалуйста, отправку запроса.'
en:
  price_on_request: 'My requests'
  price_request_send_header: 'Thank you for submitting a request. Your request has been sent to a manager and saved on your profile page "My requests"'
  price_request_send_text: 'Manager will contact you shortly. File with price requests has been sent to the email you specified.'
  go_to_profile: 'Go to profile'
  price_request_not_added_1: 'New price requests were not created.'
  price_request_not_added_2: 'Your price requests history is located on profile page'
  print_btn: 'Print price request'
  save_file_btn: 'Save price request file'
  add_comment: 'Add comment'
  confirm_delete_1: 'Are you sure you want to clear price request?'
  confirm_delete_2: 'Confirm action!'
  confirm_item_delete_1: 'Are you sure you want to delete this product?'
  confirm_item_delete_2: 'Confirm action!'
  delete: 'Delete'
  confirm_submit: 'Please confirm price request submit.'
</i18n>